/**
 * Route patterns registered in src/routes.tsx — the "SPA destination" set.
 * `:param` segments match exactly one non-empty path segment.
 *
 * Used to decide whether a link target should navigate through react-router
 * (keeps the persistent Arc shell mounted) or through a full document load
 * (monolith/Rails pages). Keep in sync with src/routes.tsx.
 */
const SPA_ROUTE_PATTERNS = [
  '/account/signin',
  '/account/signout',
  '/account/confirm',
  '/account/teacher-signup',
  '/account/add-student',
  '/hello-teacher/:token',
  '/forgot-password',
  '/new-password',
  '/error_permission',
  '/feed',
  '/arc_projects',
  '/projects',
  '/projects/new',
  '/projects/:id',
  '/projects/:id/edit',
  '/projects/:id/students/:studentId',
  '/projects/lessons/:lessonId/edit_report',
  '/projects/final_assessments/:lessonId',
  '/final_project_report/:id',
  '/reports/dashboard',
  '/reports/student',
  '/reports/general',
  '/reports/group',
  '/reports/group/analitical/:groupId',
  '/teacher/manage-students',
  '/teacher/manage-students/:studentId/activity',
  '/teacher/manage-faculty',
  '/teacher/settings',
  '/teacher/school-details',
  '/teacher/google-classroom',
  '/teacher/dashboard',
  '/learners',
  '/learners/new',
  '/learners/new/:id',
  '/guardian_room/dashboard',
  '/student_room/dashboard',
  '/admin/feature-flags',
  '/admin/connections',
  '/app/profile',
  '/app/task-tracker',
  '/app/student-tasks',
  '/app/student-task-tracker',
  '/app/development-report',
  '/app/manager/dashboard',
  '/app/resource-center',
  '/app/resource-center/:articleSlug',
]

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const patternToRegExp = (pattern: string) =>
  new RegExp(
    `^${pattern
      .split('/')
      .map(segment => (segment.startsWith(':') ? '[^/]+' : escapeRegExp(segment)))
      .join('/')}/?$`,
  )

const SPA_ROUTE_REGEXPS = SPA_ROUTE_PATTERNS.map(patternToRegExp)

/**
 * Returns true when `url` is an app-relative path handled by the SPA router
 * (src/routes.tsx). Query string and hash are ignored. Absolute URLs,
 * protocol-relative URLs and non-path values are never SPA routes.
 */
export const isSpaRoute = (url: string | null | undefined): boolean => {
  if (!url) return false
  if (url.startsWith('//')) return false
  if (!url.startsWith('/')) return false

  const [path] = url.split(/[?#]/)
  return SPA_ROUTE_REGEXPS.some(regExp => regExp.test(path))
}
